import React, { Component } from "react";
import axios from "axios";
import { BrowserRouter, Switch } from "react-router-dom";

import { verifyLogin } from "services/authProvider";
import { API_ROOT } from "config.js";

const ACCESS_TOKEN_KEY = "access-token";
let inMemoryAccessToken = null;

function safeGetStorageItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function safeSetStorageItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Safari iOS private mode can block localStorage writes.
  }
}

function safeRemoveStorageItem(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // Ignore localStorage removal failures.
  }
}

function readTokenFromCookie() {
  if (typeof document === "undefined") {
    return null;
  }

  const tokenPair = document.cookie
    .split(";")
    .map(value => value.trim())
    .find(value => value.indexOf(`${ACCESS_TOKEN_KEY}=`) === 0);

  if (!tokenPair) {
    return null;
  }

  const encodedValue = tokenPair.substring(ACCESS_TOKEN_KEY.length + 1);
  if (!encodedValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(encodedValue));
    return parsed && parsed.token ? parsed.token : null;
  } catch (error) {
    return null;
  }
}

function writeTokenCookie(data) {
  if (typeof document === "undefined") {
    return;
  }

  try {
    const encoded = encodeURIComponent(JSON.stringify(data || {}));
    document.cookie = `${ACCESS_TOKEN_KEY}=${encoded}; path=/; SameSite=Lax`;
  } catch (error) {
    // Ignore cookie write issues and fall back to in-memory token.
  }
}

function clearTokenCookie() {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${ACCESS_TOKEN_KEY}=; Max-Age=0; path=/; SameSite=Lax`;
}

function parseStoredAccessToken(rawValue) {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    return parsed && parsed.token ? parsed.token : null;
  } catch (error) {
    return null;
  }
}

export const AuthenticatedContext = React.createContext(null);
class AuthenticatedComponent extends Component {
  constructor(props) {
    super(props);
    verifyLogin().then(token => {
      this.setState({ accessToken: token });
    });
  }

  createProviderValue = () => {
    return {
      accessToken: getAccessToken(),
      axios: this.createAxiosInstance(),
      user: getUserData()
    };
  };

  createAxiosInstance = () => {
    const instance = axios.create({
      baseURL: API_ROOT,
      withCredentials: true,
      paramsSerializer: params =>
        JSON.stringify(params, { arrayFormat: "repeat", skipNulls: true })
    });
    instance.interceptors.request.use(request => {
      let token =
        this.state && this.state.accessToken != null
          ? this.state.accessToken
          : getAccessToken();

      const headers = request.headers || {};

      // Ensure Authorization header is set reliably across browsers.
      if (token) {
        headers.Authorization = `Token ${token}`;
        const isFormDataBody =
          typeof FormData !== "undefined" && request.data instanceof FormData;
        if (!isFormDataBody && !headers["Content-Type"]) {
          headers["Content-Type"] = "application/json";
        }
      }
      request.headers = headers;

      //show global loading indicator
      document.body.classList.add("loading-indicator");
      return request;
    }, Promise.reject);
    instance.interceptors.response.use(
      response => {
        // hide global loading indicator
        document.body.classList.remove("loading-indicator");
        return response;
      },
      error => {
        // hide global loading indicator
        document.body.classList.remove("loading-indicator");
        if (error.response && [401, 403].includes(error.response.status)) {
          // this.logout();
        }
        return Promise.reject(error);
      }
    );
    return instance;
  };

  render() {
    return (
      <AuthenticatedContext.Provider value={this.createProviderValue()}>
        {this.renderBody()}
      </AuthenticatedContext.Provider>
    );
  }

  renderBody() {
    if (this.state && this.state.accessToken) {
      const userData = getUserData();
      const childrenWithProps = React.Children.map(
        // eslint-disable-next-line react/prop-types
        this.props.children,
        child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { user: userData });
          }
          return child;
        }
      );
      return childrenWithProps;
    } else {
      return (
        <BrowserRouter>
          <Switch />
        </BrowserRouter>
      );
    }
  }
}
export const Authenticated = AuthenticatedComponent;

export function withAxios(Component) {
  // eslint-disable-next-line react/display-name
  return props => {
    return (
      <AuthenticatedContext.Consumer>
        {({ axios, user }) => (
          <Component {...props} axios={axios} user={user} />
        )}
      </AuthenticatedContext.Consumer>
    );
  };
}

export function getAccessToken() {
  if (inMemoryAccessToken) {
    return inMemoryAccessToken;
  }

  const tokenFromStorage = parseStoredAccessToken(
    safeGetStorageItem(ACCESS_TOKEN_KEY)
  );

  if (tokenFromStorage) {
    inMemoryAccessToken = tokenFromStorage;
    return tokenFromStorage;
  }

  const tokenFromCookie = readTokenFromCookie();
  if (tokenFromCookie) {
    inMemoryAccessToken = tokenFromCookie;
    return tokenFromCookie;
  }

  return null;
}

export function getUserData() {
  const value = safeGetStorageItem(ACCESS_TOKEN_KEY);
  if (value) {
    let data = {};
    try {
      data = JSON.parse(value);
    } catch (error) {
      return null;
    }
    return {
      username: data.username,
      user_id: data.user_id,
      roles: data.roles,
      isStaff: data.is_staff,
      activeModules: data.active_modules,
      preferences: data.preferences
    };
  }
}

export function setAccessToken(data) {
  // console.log("setAccessToken", data);
  inMemoryAccessToken = data && data.token ? data.token : null;
  safeSetStorageItem(ACCESS_TOKEN_KEY, JSON.stringify(data));
  writeTokenCookie(data);
}

export function removeAccessToken() {
  inMemoryAccessToken = null;
  safeRemoveStorageItem(ACCESS_TOKEN_KEY);
  clearTokenCookie();
}
